import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Button } from '../../../../web/components/Button';
import { DeprecatedPopover, PopoverType } from '../../../../web/components/DeprecatedPopover';
import { SuccessCheckIcon } from '../../../../web/components/svgs/icons/SuccessCheckIcon';
import { XIcon } from '../../../../web/components/svgs/icons/XIcon';
import { brandPrimary, charmGray, nameCircles } from '../../../../web/styles/colors';
import { useTwilio } from '../../../components/ai/context';
import { useWebsockets } from '../../../hooks';
import { DraggableIcon } from '../../svgs/icons/audio/DraggableIcon';
import { styleSheet } from './styles';

export const GoalsList = ({
	topicProgressions,
	scorecard,
	complete = false,
}: {
	topicProgressions: Api.IScorecardTopicProgress[];
	scorecard: Api.IScorecard;
	complete?: boolean;
}) => {
	const [hoveredTopics, setHoveredTopics] = React.useState<Api.IScorecardTopicProgress[]>([]);

	const getLabel = (topicProgression: Api.IScorecardTopicProgress) => {
		const { topics } = scorecard.scorecardTemplateVersion;
		const matchingTopic = topics.find(x => x.identifier === topicProgression.topicIdentifier);
		return matchingTopic?.label ?? topicProgression.topicIdentifier;
	};

	const getSummary = (topicProgression: Api.IScorecardTopicProgress) => {
		const { topics } = scorecard.scorecardTemplateVersion;
		const matchingTopic = topics.find(x => x.identifier === topicProgression.topicIdentifier);
		return matchingTopic?.summary ?? '';
	};

	const onHover = (topicProgression: Api.IScorecardTopicProgress) => {
		if (!hoveredTopics.includes(topicProgression)) {
			setHoveredTopics([...hoveredTopics, topicProgression]);
		}
	};

	const onLeave = (topicProgression: Api.IScorecardTopicProgress) => {
		setHoveredTopics(hoveredTopics.filter(x => x !== topicProgression));
	};

	return (
		<>
			{topicProgressions.map(topicProgression => {
				const isHovering = hoveredTopics.includes(topicProgression);
				return (
					<DeprecatedPopover
						key={`topic-progression-${topicProgression.topicIdentifier}`}
						anchor={
							<div
								className={css(styleSheet.progression)}
								key={topicProgression.topicIdentifier}
								onMouseOver={() => onHover(topicProgression)}
								onMouseLeave={() => onLeave(topicProgression)}
							>
								<SuccessCheckIcon fillColor={complete ? null : nameCircles} ringColor={complete ? null : '#F9F9F9'} />
								<span className={isHovering ? css(styleSheet.isHovering) : null}>{getLabel(topicProgression)}</span>
							</div>
						}
						isOpen={isHovering}
						type={PopoverType.white}
						preferredPlacement='above'
					>
						<div className={css(styleSheet.topicPopover)}>{getSummary(topicProgression)}</div>
					</DeprecatedPopover>
				);
			})}
		</>
	);
};

export const LiveScorecard = ({
	scorecard,
	setLastPhoneCall,
	gkScorecard,
	dmScorecard,
	onToggleScorecard,
	sdrName,
	vertical,
}: {
	scorecard: Api.IScorecard;
	setLastPhoneCall?: (phoneCall: Api.IPhoneCall | null) => void;
	gkScorecard?: Api.IScorecard;
	dmScorecard?: Api.IScorecard;
	onToggleScorecard?: (scorecard: Api.IScorecard) => void;
	sdrName: string;
	vertical: string;
}) => {
	const {
		completeTopicIdentifiers,
		setCompleteTopicIdentifiers,
		incompleteTopicIdentifiers,
		setIncompleteTopicIdentifiers,
		realtimeTranscriptionUtterances,
		setRealtimeTranscriptionUtterances,
	} = useTwilio();
	const realtimeTranscriptionUtterancesRef = React.useRef<HTMLDivElement>(null);
	const [callParticipant, setCallParticipant] = React.useState<string>(
		scorecard.persona === Api.ScorecardPersona.DecisionMaker ? 'Decision Maker' : 'Gatekeeper'
	);

	const onHover = () => {
		setLiveScoreHover(true);
	};

	const onLeave = () => {
		setLiveScoreHover(false);
	};
	const [liveScoreHover, setLiveScoreHover] = React.useState<boolean>(null);
	const webSockets = useWebsockets();
	const webSocketsConnected =
		(webSockets?.connectionStatus ?? Api.RemoteEventsConnectionStatus.Disconnected) ===
		Api.RemoteEventsConnectionStatus.Connected;

	const hasNecessarySubscriptions =
		webSockets.hasSubscription('callSuggestionHintEvent') && webSockets.hasSubscription('scorecardEvent');
	const webSocketsFullyConnected = webSocketsConnected && hasNecessarySubscriptions;

	const completedPercent = Math.floor(
		(completeTopicIdentifiers.length / (completeTopicIdentifiers.length + incompleteTopicIdentifiers.length)) * 100
	);

	const completeProgressions = scorecard.topicProgressions.filter(
		x => completeTopicIdentifiers.indexOf(x.topicIdentifier) > -1
	);
	const incompleteProgressions = scorecard.topicProgressions.filter(
		x => incompleteTopicIdentifiers.indexOf(x.topicIdentifier) > -1
	);

	// Auto-scroll transcription utterances
	React.useEffect(() => {
		if (realtimeTranscriptionUtterancesRef.current) {
			realtimeTranscriptionUtterancesRef.current.scrollTop = realtimeTranscriptionUtterancesRef.current.scrollHeight;
		}
	}, [realtimeTranscriptionUtterances]);

	const handleClose = () => {
		setLastPhoneCall(null);
	};

	const handleToggleScorecard = () => {
		if (!onToggleScorecard) return;

		// If current scorecard is GK, toggle to DM, and vice versa
		if (scorecard.persona === Api.ScorecardPersona.GateKeeper && dmScorecard) {
			onToggleScorecard(dmScorecard);
			setCallParticipant('Decision Maker');
			// Add a message to realtimeTranscriptionUtterances to notify that we're now speaking with a DM
			setRealtimeTranscriptionUtterances((prev: string[]) => [...prev, 'System: Now speaking with a Decision Maker']);
		} else if (scorecard.persona === Api.ScorecardPersona.DecisionMaker && gkScorecard) {
			onToggleScorecard(gkScorecard);
			setCallParticipant('Gatekeeper');
			// Add a message to realtimeTranscriptionUtterances to notify that we're now speaking with a GK
			setRealtimeTranscriptionUtterances((prev: string[]) => [...prev, 'System: Now speaking with a Gatekeeper']);
		}
	};

	React.useEffect(() => {
		if (!scorecard?.topicProgressions?.length) {
			return;
		}

		setCompleteTopicIdentifiers(scorecard.topicProgressions.filter(x => x.isCompleted).map(x => x.topicIdentifier));
		setIncompleteTopicIdentifiers(scorecard.topicProgressions.filter(x => !x.isCompleted).map(x => x.topicIdentifier));
	}, [scorecard, setCompleteTopicIdentifiers, setIncompleteTopicIdentifiers]);

	return (
		<div className={css(styleSheet.root)}>
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.oneQuarter)}>
					<div className={css(styleSheet.currentScore)}>
						<span>Score: </span>
						<span className={css(completedPercent > 79 ? styleSheet.green : styleSheet.red)}>{completedPercent}%</span>
					</div>
				</div>

				<DeprecatedPopover
					key='live-score-card-popover'
					anchor={
						<div className={css(styleSheet.oneHalf)} onMouseEnter={() => onHover()}>
							<div className={css(webSocketsFullyConnected ? styleSheet.greenCircle : styleSheet.redCircle)}>⬤</div>
							<h5>Live Score Card</h5>
						</div>
					}
					isOpen={liveScoreHover}
					dismissOnClickOutside={true}
					onRequestClose={onLeave}
					type={PopoverType.white}
					preferredPlacement='above'
				>
					<div className={css(styleSheet.topicPopover)}>
						Websockets: {webSocketsConnected ? 'Connected' : 'Disconnected'}
						<br />
						Subscriptions: {hasNecessarySubscriptions ? 'Subscribed' : 'Not Subscribed'}
						<br />
						Last Message Received: {moment(webSockets.lastMessageReceivedTime).format('h:mm:ss a')}
						<br />
						Last Suggestion Message Received:{' '}
						{webSockets.lastMessageReceivedTimeBySubscriptionName('callSuggestionHintEvent')
							? moment(webSockets.lastMessageReceivedTimeBySubscriptionName('callSuggestionHintEvent')).format(
									'h:mm:ss a'
								)
							: 'Never'}
						<br />
						Last Scorecard Message Received:{' '}
						{webSockets.lastMessageReceivedTimeBySubscriptionName('scorecardEvent')
							? moment(webSockets.lastMessageReceivedTimeBySubscriptionName('scorecardEvent')).format('h:mm:ss a')
							: 'Never'}
					</div>
				</DeprecatedPopover>

				{setLastPhoneCall && (
					<div className={css(styleSheet.iconGroup)}>
						<div className='sales-ai-drag-handle'>
							<DraggableIcon fill={brandPrimary} />
						</div>
						{onToggleScorecard && (gkScorecard || dmScorecard) && (
							<Button kind='secondary' size='small' onClick={handleToggleScorecard}>
								Switch to {scorecard.persona === Api.ScorecardPersona.GateKeeper ? 'DM' : 'GK'}
							</Button>
						)}
						<button onClick={handleClose} className={css(styleSheet.iconButton)}>
							<XIcon color={charmGray} />
						</button>
					</div>
				)}
			</div>

			<div className={css(styleSheet.progressions)}>
				<GoalsList topicProgressions={completeProgressions} complete scorecard={scorecard} />
				<GoalsList topicProgressions={incompleteProgressions} scorecard={scorecard} />
			</div>

			<div className={css(styleSheet.realtimeTranscriptionUtterances)} ref={realtimeTranscriptionUtterancesRef}>
				{realtimeTranscriptionUtterances.length === 0 && (
					<div>
						Call started between {sdrName} and a {callParticipant} in the {vertical} vertical.
					</div>
				)}
				{realtimeTranscriptionUtterances.map((utterance: string, index: number) => {
					return <div key={`${utterance}-${index}`}>{utterance}</div>;
				})}
			</div>
		</div>
	);
};
